import { Socket, Manager } from "socket.io-client";
import { MessageParams } from "../../domain/entities/addMessageSocket";
import { MessageReadParams } from "../../domain/entities/updateMessageReadSocket";
import { NewMessageResponse } from "../../domain/entities/onNewMessageSocket";
import { UpdatedMessageResponse } from "../../domain/entities/onUpdatedMessageSocket";
import { ChatMessage } from "../../domain/entities/getMessages";
import { UpdatedChatStatusSocket } from "../../domain/entities/UpdatedChatStatusSocket";
import { UpdateChatStatusSocket } from "../../domain/entities/UpdateChatStatusSocket";
import { getApiErrorMessage } from "../../../../core/utilities/ApiServices";

export const chatSocketErrorChannel = new EventTarget();
export const chatSocketLifecycleChannel = new EventTarget();

export interface ChatSocketDataSource {
	connectSocket(): Promise<void>;
	disconnectSocket(): Promise<void>;
	addMessage(message: MessageParams): Promise<void>;
	onNewMessage(callback: (data: NewMessageResponse) => void): void;
	updateMessageRead(params: MessageReadParams): Promise<void>;
	onUpdateMessage(callback: (data: UpdatedMessageResponse[]) => void): void;
	emitTempMessage(messageId: string): Promise<void>;
	onTempMessageSeen(callback: (data: ChatMessage) => void): void;
	updateChatStatus(data: UpdateChatStatusSocket): Promise<void>;
	onUpdatedChatStatus(callback: (data: UpdatedChatStatusSocket) => void): void;
}

export class ChatSocketDataSourceImpl implements ChatSocketDataSource {
	private manager: Manager | null = null;
	private socket: Socket | null = null;
	private connectPromise: Promise<void> | null = null;
	private hasConnectedOnce = false;
	private onNewMessageCallback: ((data: NewMessageResponse) => void) | null = null;
	private onUpdateMessageCallback:
		| ((data: UpdatedMessageResponse[]) => void)
		| null = null;
	private onTempMessageSeenCallback: ((data: ChatMessage) => void) | null = null;
	private onUpdatedChatStatusCallback: ((data: UpdatedChatStatusSocket) => void) | null = null;

	async connectSocket(): Promise<void> {
		const token = localStorage.getItem("userToken");
		if (!token) {
			throw new Error("No se encontró el token");
		}

		await this.disconnectSocket();

		this.manager = new Manager(`${process.env.REACT_APP_UNJABBED}/socket.io/socket.io.js`, {
			transports: ["websocket"],
			autoConnect: false,
			reconnection: true,
			reconnectionAttempts: Infinity,
			reconnectionDelay: 1000,
			reconnectionDelayMax: 5000,
			timeout: 10000,
			extraHeaders: {
				authorization: `Bearer ${token}`,
			},
		});

		this.socket = this.manager.socket("/chat", { auth: { token } });
		this.registerSocketListeners(this.socket);
		this.socket.connect();
		await this.waitForSocketConnection();
	}

	private registerSocketListeners(socket: Socket): void {
		socket.on("connect", () => {
			if (!this.hasConnectedOnce) {
				this.hasConnectedOnce = true;
				return;
			}

			chatSocketLifecycleChannel.dispatchEvent(new Event("reconnect"));
		});
		socket.on("new_message", (data: NewMessageResponse) => {
			if (this.onNewMessageCallback) {
				this.onNewMessageCallback(data);
			}
		});
		socket.on("updated_message", (data: UpdatedMessageResponse[]) => {
			if (this.onUpdateMessageCallback) {
				this.onUpdateMessageCallback(data);
			}
		});
		socket.on("temp_message_seen", (data) => {
			if (this.onTempMessageSeenCallback) {
				this.onTempMessageSeenCallback(data);
			}
		});
		socket.on("updated_chat_status", (data: UpdatedChatStatusSocket) => {
			if (this.onUpdatedChatStatusCallback) {
				this.onUpdatedChatStatusCallback(data);
			}
		});
		socket.on("error", (data) => {
			const message = getApiErrorMessage(data);
			chatSocketErrorChannel.dispatchEvent(
				new CustomEvent("error", { detail: message })
			);
			console.error("Error en el socket:", data);
		});
		socket.on("connect_error", (data) => {
			const message = getApiErrorMessage(data);
			chatSocketErrorChannel.dispatchEvent(
				new CustomEvent("error", { detail: message })
			);
			console.error("Error conectando el socket:", data);
		});
	}

	private waitForSocketConnection(): Promise<void> {
		if (!this.socket) {
			return Promise.reject(new Error("Socket no inicializado"));
		}
		if (this.socket.connected) {
			return Promise.resolve();
		}
		if (this.connectPromise) {
			return this.connectPromise;
		}

		this.connectPromise = new Promise((resolve, reject) => {
			const socket = this.socket!;
			const cleanup = () => {
				socket.off("connect", handleConnect);
				socket.off("connect_error", handleConnectError);
				this.connectPromise = null;
			};
			const handleConnect = () => {
				cleanup();
				resolve();
			};
			const handleConnectError = (error: unknown) => {
				cleanup();
				reject(error);
			};

			socket.once("connect", handleConnect);
			socket.once("connect_error", handleConnectError);
		});

		return this.connectPromise;
	}

	private async ensureConnectedSocket(): Promise<Socket> {
		if (!this.socket) {
			await this.connectSocket();
		} else if (!this.socket.connected) {
			this.socket.connect();
			await this.waitForSocketConnection();
		}

		if (!this.socket) {
			throw new Error("Socket no disponible");
		}

		return this.socket;
	}

	disconnectSocket(): Promise<void> {
		return new Promise((resolve) => {
			if (this.socket) {
				this.socket.removeAllListeners();
				this.socket.disconnect();
				this.socket = null;
			}
			this.manager = null;
			this.connectPromise = null;
			this.hasConnectedOnce = false;
			resolve();
		});
	}

	async addMessage(message: MessageParams): Promise<void> {
		const socket = await this.ensureConnectedSocket();
		socket.emit("add_message", message);
	}

	onNewMessage(callback: (data: NewMessageResponse) => void): void {
		this.onNewMessageCallback = callback;
	}

	async updateMessageRead(params: MessageReadParams): Promise<void> {
		const socket = await this.ensureConnectedSocket();
		socket.emit("update_message_read", params);
	}

	onUpdateMessage(callback: (data: UpdatedMessageResponse[]) => void): void {
		this.onUpdateMessageCallback = callback;
	}

	async emitTempMessage(messageId: string): Promise<void> {
		const socket = await this.ensureConnectedSocket();
		socket.emit("temp_message", messageId);
	}

	onTempMessageSeen(callback: (data: ChatMessage) => void): void {
		this.onTempMessageSeenCallback = callback;
	}

	
	async updateChatStatus(data: UpdateChatStatusSocket): Promise<void> {
		const socket = await this.ensureConnectedSocket();
		socket.emit("update_chat_status", data);
	}
	onUpdatedChatStatus(callback: (data: UpdatedChatStatusSocket) => void): void {
		this.onUpdatedChatStatusCallback = callback;
	}
}
